import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { GoTo, Direction } from 'components/common/GoTo';
import Wahanda from 'common/wahanda';
import { trackEvent, SalesReportOldAnalytics } from 'common/analytics';
import { parseSalesFiltersFromWindowHash } from 'src/url-states/reports-sales';
import type { EnrichedLegacySalesReportView, SalesReportFilters } from 'services/sales-report';
import {
  buildSalesReportDateRangeTitle,
  enrichSalesReportTemplateData,
  fetchSalesReport,
  getSalesReportVenueName,
  getSalesReportCurrentTime,
  getSalesReportDateFilters,
  getSalesReportDateRange,
  navigateSalesReportHash,
  parseSalesReportStartDateFromApiString,
} from 'services/sales-report';
import { SalesReportContent } from './SalesReportContent';
import style from './style.scss';

const computeSalesReportMaxSelectableDate = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
};

const SalesReportInner = () => {
  const [yesterday, setYesterday] = useState(() => computeSalesReportMaxSelectableDate());
  const [periodGranularity, setPeriodGranularity] = useState('day');
  const [startDate, setStartDate] = useState(() => computeSalesReportMaxSelectableDate());
  const [selectionOpen, setSelectionOpen] = useState(false);
  const [enrichedReport, setEnrichedReport] = useState<EnrichedLegacySalesReportView | null>(null);
  const [datepickerReady, setDatepickerReady] = useState(false);
  const [hydratedFromHash, setHydratedFromHash] = useState(false);

  const dateInputRef = useRef<HTMLInputElement>(null);
  const reportContentRef = useRef<HTMLDivElement>(null);
  const closedTitleRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const currentFetchRequestRef = useRef<JQueryXHR | null>(null);
  const fetchRequestIdRef = useRef(0);
  const startDateRef = useRef(startDate);
  const periodRef = useRef(periodGranularity);
  const yesterdayRef = useRef(yesterday);
  const currentFiltersRef = useRef<SalesReportFilters>(
    getSalesReportDateFilters(
      getSalesReportDateRange({
        startDate: yesterday,
        yesterday,
        periodGranularity: 'day',
      }),
    ),
  );
  const weekGranularityPreviouslySelectedRef = useRef(false);

  useLayoutEffect(() => {
    startDateRef.current = startDate;
    periodRef.current = periodGranularity;
    yesterdayRef.current = yesterday;
  }, [startDate, periodGranularity, yesterday]);

  useEffect(() => {
    const sync = () => {
      const next = computeSalesReportMaxSelectableDate();
      setYesterday((prev) =>
        Wahanda.Date.toApiString(prev) === Wahanda.Date.toApiString(next) ? prev : next,
      );
    };
    const id = window.setInterval(sync, 60_000);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        sync();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  useEffect(() => {
    setStartDate((sd) => {
      if (Wahanda.Date.compare(sd, yesterday, { onlyDates: true }) > 0) {
        return Wahanda.Date.min(sd, yesterday);
      }
      return sd;
    });
  }, [yesterday]);

  const selectorTitle = useMemo(
    () =>
      buildSalesReportDateRangeTitle({
        periodGranularity,
        dateRange: getSalesReportDateRange({
          startDate,
          yesterday,
          periodGranularity,
        }),
      }),
    [periodGranularity, startDate, yesterday],
  );

  const fetchReport = useCallback(() => {
    const sd = startDateRef.current;
    const pg = periodRef.current;
    const y = yesterdayRef.current;
    const filters = getSalesReportDateFilters(
      getSalesReportDateRange({
        startDate: sd,
        yesterday: y,
        periodGranularity: pg,
      }),
    );
    const requestId = fetchRequestIdRef.current + 1;
    fetchRequestIdRef.current = requestId;

    if (currentFetchRequestRef.current && currentFetchRequestRef.current.readyState !== 4) {
      currentFetchRequestRef.current.abort();
    }

    currentFiltersRef.current = filters;

    const maskHost = reportContentRef.current;
    if (maskHost) {
      $(maskHost).loadmask();
    }

    navigateSalesReportHash({ periodGranularity: pg, startDate: sd });

    currentFetchRequestRef.current = fetchSalesReport(filters);
    currentFetchRequestRef.current
      .done((response) => {
        if (requestId !== fetchRequestIdRef.current) {
          return;
        }

        const dateRange = getSalesReportDateRange({
          startDate: sd,
          yesterday: y,
          periodGranularity: pg,
        });
        const enriched = enrichSalesReportTemplateData({
          reportData: response || {},
          dateRange,
          periodGranularity: pg,
          venueName: getSalesReportVenueName(),
          currentTime: getSalesReportCurrentTime(),
        });
        setEnrichedReport(enriched);
        if (maskHost) {
          $(maskHost).unloadmask();
        }
      })
      .fail((_xhr, status) => {
        if (requestId !== fetchRequestIdRef.current || status === 'abort') {
          return;
        }
        if (maskHost) {
          $(maskHost).unloadmask();
        }
      });
  }, []);

  useLayoutEffect(() => {
    const hash = parseSalesFiltersFromWindowHash();
    if (hash) {
      setPeriodGranularity(hash.periodGranularity);
      const fromHash = parseSalesReportStartDateFromApiString(hash.startDate);
      if (fromHash) {
        setStartDate(fromHash);
      }
    }
    setHydratedFromHash(true);
  }, []);

  useEffect(() => {
    SalesReportOldAnalytics.trackPageView();
  }, []);

  useEffect(() => {
    $('.upgrade-overlay').removeClass('sales').hide();
    $('#sales').show();
  }, []);

  useEffect(() => {
    if (!hydratedFromHash || !datepickerReady) {
      return;
    }
    fetchReport();
  }, [datepickerReady, fetchReport, hydratedFromHash]);

  useEffect(() => {
    const inputEl = dateInputRef.current;
    if (!inputEl) {
      return;
    }
    const $input = $(inputEl);

    const onBody = (event: { type: string; target: EventTarget | null; keyCode?: number }) => {
      const closedEl = closedTitleRef.current;
      const formEl = formRef.current;
      const $closedTitle = closedEl ? $(closedEl) : $();
      const $form = formEl ? $(formEl) : $();
      if (event.type === 'click') {
        if (
          !$closedTitle.is(event.target) &&
          $closedTitle.has((event.target as unknown) as Element).length === 0 &&
          $form.has((event.target as unknown) as Element).length === 0 &&
          $('.ui-datepicker').filter(':visible').length === 0
        ) {
          setSelectionOpen(false);
        }
      } else if (
        event.type === 'keydown' &&
        event.keyCode === 27 &&
        $input.datepicker('widget').is(':not(:visible)')
      ) {
        setSelectionOpen(false);
      }
    };

    $('body').on('click.salesLegacyReport keydown.salesLegacyReport', onBody);

    $input.venueDatepicker({
      maxDate: yesterdayRef.current,
      beforeShow: () => {
        const pg = periodRef.current;
        $input.datepicker('option', {
          maxDate: yesterdayRef.current,
          changeMonth: pg === 'month',
          changeYear: pg === 'month',
        });
        $('.ui-datepicker').toggleClass('ui-month-year-only', pg === 'month');
      },
      onChangeMonthYear(year: number, month: number) {
        if (periodRef.current === 'month') {
          let startOfMonth = new Date(year, month - 1, 1);
          if (Wahanda.Date.isFuture(startOfMonth, true)) {
            startOfMonth = new Date();
            startOfMonth.setDate(1);
          }
          $input.datepicker('setDate', startOfMonth);
          startDateRef.current = startOfMonth;
          setStartDate(startOfMonth);
        }
      },
      onClose() {
        $input.blur();
      },
      onSelect() {
        const newDate = $input.datepicker('getDate') as Date;
        startDateRef.current = newDate;
        setStartDate(newDate);
      },
    });
    $input.datepicker('setDate', startDateRef.current);
    setDatepickerReady(true);

    return () => {
      $('body').off('.salesLegacyReport');
      if ($input.data('datepicker')) {
        $input.datepicker('destroy');
      }
    };
  }, []);

  useEffect(() => {
    const inputEl = dateInputRef.current;
    if (!inputEl) {
      return;
    }
    const $input = $(inputEl);
    if ($input.length && $input.data('datepicker')) {
      $input.datepicker('option', { maxDate: yesterday });
    }
  }, [yesterday]);

  useEffect(() => {
    const inputEl = dateInputRef.current;
    if (!inputEl) {
      return;
    }
    const $input = $(inputEl);
    if ($input.length && $input.data('datepicker')) {
      const pg = periodGranularity;
      $input.datepicker('option', {
        changeMonth: pg === 'month',
        changeYear: pg === 'month',
      });
    }
  }, [periodGranularity]);

  useEffect(() => {
    const inputEl = dateInputRef.current;
    if (!inputEl) {
      return;
    }
    const $input = $(inputEl);
    if ($input.length && $input.data('datepicker')) {
      $input.datepicker('setDate', startDate);
    }
  }, [startDate]);

  useEffect(() => {
    return () => {
      if (currentFetchRequestRef.current && currentFetchRequestRef.current.readyState !== 4) {
        currentFetchRequestRef.current.abort();
      }
    };
  }, []);

  const onPeriodChange = (value: string) => {
    periodRef.current = value;
    setPeriodGranularity(value);
    trackEvent('sales-report', 'click', `period-${value}`);
    const inputEl = dateInputRef.current;
    if (!inputEl) {
      return;
    }
    const $input = $(inputEl);
    if (!$input.length) {
      return;
    }

    if (value === 'week' && !weekGranularityPreviouslySelectedRef.current) {
      const weekStart = Wahanda.Date.getWeekStartDate($input.datepicker('getDate'));
      $input.datepicker('setDate', weekStart);
      startDateRef.current = weekStart;
      setStartDate(weekStart);
      weekGranularityPreviouslySelectedRef.current = true;
    }

    if (value === 'month') {
      const startOfMonth = $input.datepicker('getDate') as Date;
      startOfMonth.setDate(1);
      $input.datepicker('setDate', startOfMonth);
      startDateRef.current = startOfMonth;
      setStartDate(startOfMonth);
    }
  };

  const onApply = () => {
    currentFiltersRef.current = getSalesReportDateFilters(
      getSalesReportDateRange({
        startDate: startDateRef.current,
        yesterday: yesterdayRef.current,
        periodGranularity: periodRef.current,
      }),
    );
    trackEvent('sales-reports', 'edit', currentFiltersRef.current);
    setSelectionOpen(false);
    fetchReport();
  };

  const toggleDateInputWrapper = (event: React.MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).nodeName === 'INPUT') {
      return;
    }
    const inputEl = dateInputRef.current;
    if (!inputEl) {
      return;
    }
    const $input = $(inputEl);
    if ($input.length === 1) {
      const widget = $input.datepicker('widget');
      if (widget.is(':visible')) {
        $input.datepicker('hide');
      } else {
        $input.datepicker('show');
      }
    }
  };

  return (
    <div id="sales" className="section-main ui-tabs-hide">
      <div className="report-actions">
        <div className="date-range-selector">
          <div
            ref={closedTitleRef}
            className={selectionOpen ? 'current hidden' : 'current'}
            onClick={() => setSelectionOpen((o) => !o)}
            role="presentation"
          >
            <div className="icon icons-date-selector" />
            <div className="value">{selectorTitle}</div>
            <div className="arrow icons-arrow-bottom2" />
          </div>

          <div className={selectionOpen ? 'selection' : 'selection hidden'}>
            <div className="title" onClick={() => setSelectionOpen((o) => !o)} role="presentation">
              <div className="icon icons-date-selector" />
              <div className="value">
                {Wahanda.lang.reports.salesReports.dateRange.labels.selectRange}
              </div>
            </div>

            <form ref={formRef}>
              <div className="period-granularity">
                <label>
                  <input
                    type="radio"
                    name="period-granularity"
                    value="day"
                    checked={periodGranularity === 'day'}
                    onChange={() => onPeriodChange('day')}
                  />
                  {Wahanda.lang.reports.salesReports.dateRange.labels.day}
                </label>
                <label>
                  <input
                    type="radio"
                    name="period-granularity"
                    value="week"
                    checked={periodGranularity === 'week'}
                    onChange={() => onPeriodChange('week')}
                  />
                  {Wahanda.lang.reports.salesReports.dateRange.labels.week}
                </label>
                <label>
                  <input
                    type="radio"
                    name="period-granularity"
                    value="month"
                    checked={periodGranularity === 'month'}
                    onChange={() => onPeriodChange('month')}
                  />
                  {Wahanda.lang.reports.salesReports.dateRange.labels.month}
                </label>
              </div>

              <div className="date">
                <label>
                  {periodGranularity === 'day' && (
                    <span className="v-day">
                      {Wahanda.lang.reports.salesReports.dateRange.dateLabel.day}
                    </span>
                  )}
                  {periodGranularity === 'week' && (
                    <span className="v-week">
                      {Wahanda.lang.reports.salesReports.dateRange.dateLabel.week}
                    </span>
                  )}
                  {periodGranularity === 'month' && (
                    <span className="v-month">
                      {Wahanda.lang.reports.salesReports.dateRange.dateLabel.month}
                    </span>
                  )}
                </label>

                <div
                  className="txt-input date-input"
                  onClick={toggleDateInputWrapper}
                  role="presentation"
                >
                  <input
                    ref={dateInputRef}
                    type="text"
                    name=""
                    className="datepicker"
                    defaultValue=""
                    id=""
                    readOnly
                  />
                </div>
              </div>

              <div className="buttons">
                <span
                  className="action cancel"
                  onClick={() => setSelectionOpen(false)}
                  role="presentation"
                >
                  <span className="icon icons-action-cancel" />
                  <span className="value">{Wahanda.lang.shared.cancel}</span>
                </span>

                <span className="action main" onClick={onApply} role="presentation">
                  <span className="icon icons-action-ok" />
                  <span className="value">
                    {Wahanda.lang.reports.salesReports.dateRange.buttons.showReport}
                  </span>
                </span>
              </div>
            </form>
          </div>
        </div>
      </div>

      <GoTo className={style.goBackLink} href="/" direction={Direction.backward}>
        {Wahanda.lang.reports.tabs.reports}
      </GoTo>

      <div ref={reportContentRef} className="report-content">
        {enrichedReport ? <SalesReportContent data={enrichedReport} /> : null}
      </div>
    </div>
  );
};

export default function SalesReport() {
  if (!Wahanda.Permissions.viewReports()) {
    return null;
  }
  return <SalesReportInner />;
}
